import React, { ErrorInfo, ReactNode } from "react";
import { useLogMonitoring } from "../hooks/use-log-monitoring";
import { IndexedDBProvider } from "../providers/indexed-db-provider";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

// Separate ErrorFallback component that uses IndexedDBProvider
const ErrorFallbackWithProvider: React.FC<{
    error: Error | null;
    errorInfo: ErrorInfo | null;
    onReset: () => void;
}> = ({ error, errorInfo, onReset }) => {
    return (
        <IndexedDBProvider>
            <ErrorFallback error={error} errorInfo={errorInfo} onReset={onReset} />
        </IndexedDBProvider>
    );
};

// The actual ErrorFallback component that uses useLogMonitoring
const ErrorFallback: React.FC<{
    error: Error | null;
    errorInfo: ErrorInfo | null;
    onReset: () => void;
}> = ({ error, errorInfo, onReset }) => {
    const { addLog } = useLogMonitoring();
    const hasLogged = React.useRef(false);

    React.useEffect(() => {
        if (error && !hasLogged.current && !error.message.startsWith("Uncaught")) {
            addLog({
                type: "error",
                message: error.message,
                stack: error.stack || errorInfo?.componentStack || "No stack trace available",
            });
            hasLogged.current = true;
        }
    }, [error, errorInfo, addLog]);

    const errorMessage = error?.message?.replace(/^Uncaught (Error: )?/, "") || "یک خطای غیرمنتظره رخ داده است";

    return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 flex items-center justify-center p-4">
            <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 backdrop-blur-sm backdrop-filter bg-opacity-90">
                <div className="flex flex-col items-center text-center space-y-6">
                    <div className="relative">
                        <div className="absolute inset-0 animate-ping">
                            <AlertTriangle className="w-16 h-16 text-red-500/30" />
                        </div>
                        <AlertTriangle className="w-16 h-16 text-red-500 relative" />
                    </div>

                    <div className="space-y-4">
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">خطایی رخ داده است</h1>
                        <p className="text-gray-600 dark:text-gray-300">{errorMessage}</p>
                    </div>

                    {errorInfo?.componentStack && (
                        <div className="w-full mt-6">
                            <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4 overflow-auto max-h-48">
                                <pre className="text-sm text-gray-700 dark:text-gray-300 text-left whitespace-pre-wrap font-mono">
                                    {errorInfo.componentStack}
                                </pre>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={onReset}
                        className="group relative inline-flex items-center justify-center px-8 py-3 text-lg font-medium text-white transition-all duration-200 bg-red-500 hover:bg-red-600 rounded-xl hover:shadow-xl space-x-2 space-x-reverse">
                        <span className="relative">تلاش مجدد</span>
                        <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-300" />
                    </button>
                </div>
            </div>
        </div>
    );
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);

        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        this.setState({ errorInfo });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render() {
        if (this.state.hasError) {
            return <ErrorFallbackWithProvider error={this.state.error} errorInfo={this.state.errorInfo} onReset={this.handleReset} />;
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
